/**
 * BulkActionsBar - Premium Design System v2.1
 * Barra de ações em massa com design pill/chip compacto
 *
 * Features:
 * - Pill shape compacto (não expande)
 * - Mobile: Todas ações no menu dropdown
 * - Tablet: 1 ação + menu
 * - Desktop: maxVisibleActions + menu
 * - Dark/Light mode via tokens
 */

import React, { memo, useState, useMemo } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { usePremiumTokens } from '../../theme/usePremiumTokens';
import { useTranslation } from '../LocalizationProvider';

const BulkActionsBar = memo(({
  selectedCount,
  onClearSelection,
  onDelete,
  deleteDisabled = false,
  deleteLabel,
  customActions = [],
  loading = false,
  entityName,
  maxVisibleActions = 2,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const theme = useTheme();

  // Breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Early return if nothing selected
  if (selectedCount === 0) {
    return null;
  }

  // Text
  const displayDeleteLabel = deleteLabel || t('sharedRemove') || 'Excluir';
  const selectedText = `${selectedCount} ${entityName || t('sharedSelected') || 'selecionado(s)'}`;

  // Responsive: How many actions to show inline
  // Mobile (xs): 0 inline, all in menu
  // Tablet (sm-md): 1 inline + menu
  // Desktop (lg+): maxVisibleActions inline + menu
  const effectiveMaxVisible = isMobile ? 0 : isTablet ? 1 : maxVisibleActions;

  // Build all actions array
  const allActions = useMemo(() => {
    const actions = [...customActions];
    if (onDelete) {
      actions.push({
        key: '__delete__',
        label: displayDeleteLabel,
        icon: <DeleteIcon fontSize="small" />,
        onClick: onDelete,
        disabled: deleteDisabled,
        color: tokens?.semantic?.error,
        isDanger: true,
      });
    }
    return actions;
  }, [customActions, onDelete, displayDeleteLabel, deleteDisabled, tokens]);

  // Split visible vs overflow
  const visibleActions = allActions.slice(0, effectiveMaxVisible);
  const overflowActions = allActions.slice(effectiveMaxVisible);
  const hasOverflow = overflowActions.length > 0;

  // Colors
  const accentColor = tokens?.accent?.primary || '#00d4aa';
  const pillBg = `${accentColor}15`;
  const pillBorder = `${accentColor}30`;

  return (
    <Box
      component="span"
      sx={{
        // Force inline-block behavior - NEVER expand
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: 0.5,
        // Pill styling
        px: { xs: 1, sm: 1.5 },
        py: 0.5,
        borderRadius: '9999px',
        bgcolor: pillBg,
        border: `1px solid ${pillBorder}`,
        // Prevent expansion
        width: 'auto',
        maxWidth: '100%',
        flex: '0 0 auto',
      }}
    >
      {/* Count Chip */}
      <Chip
        label={selectedText}
        size="small"
        sx={{
          height: 24,
          bgcolor: accentColor,
          color: '#0d1117',
          fontWeight: 600,
          fontSize: { xs: '0.7rem', sm: '0.75rem' },
          '& .MuiChip-label': {
            px: 1,
          },
        }}
      />

      {/* Visible Actions - Desktop/Tablet only */}
      {visibleActions.map((action) => (
        <Button
          key={action.key}
          size="small"
          startIcon={action.icon}
          onClick={action.onClick}
          disabled={loading || action.disabled}
          sx={{
            color: action.color || tokens?.text?.primary,
            textTransform: 'none',
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            whiteSpace: 'nowrap',
            minWidth: 'auto',
            px: 1,
            py: 0.25,
            '& .MuiButton-startIcon': {
              mr: 0.5,
            },
            '&:hover': {
              bgcolor: `${action.color || accentColor}20`,
            },
          }}
        >
          {action.label}
        </Button>
      ))}

      {/* Overflow Menu */}
      {hasOverflow && (
        <>
          <IconButton
            size="small"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            disabled={loading}
            sx={{
              color: tokens?.text?.secondary,
              p: 0.5,
              '&:hover': {
                bgcolor: `${accentColor}20`,
              },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  bgcolor: tokens?.surface?.elevated,
                  borderRadius: tokens?.radius?.lg,
                  border: `1px solid ${tokens?.border?.subtle}`,
                  minWidth: 180,
                  mt: 0.5,
                  boxShadow: tokens?.shadows?.lg,
                },
              },
            }}
          >
            {overflowActions.map((action, index) => [
              // Divider before danger actions
              action.isDanger && index > 0 && (
                <Divider key={`divider-${action.key}`} sx={{ my: 0.5 }} />
              ),
              <MenuItem
                key={action.key}
                onClick={() => {
                  setMenuAnchor(null);
                  action.onClick?.();
                }}
                disabled={loading || action.disabled}
                sx={{
                  color: action.color || tokens?.text?.primary,
                  py: 1,
                  '&:hover': {
                    bgcolor: `${action.color || accentColor}15`,
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                  {action.icon}
                </ListItemIcon>
                <ListItemText
                  primary={action.label}
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              </MenuItem>,
            ])}
          </Menu>
        </>
      )}

      {/* Close/Clear Button */}
      <IconButton
        size="small"
        onClick={onClearSelection}
        disabled={loading}
        aria-label={t('sharedCancel') || 'Limpar seleção'}
        sx={{
          color: tokens?.text?.secondary,
          p: 0.5,
          ml: 0.25,
          '&:hover': {
            bgcolor: `${tokens?.semantic?.error}20`,
            color: tokens?.semantic?.error,
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
});

BulkActionsBar.displayName = 'BulkActionsBar';

export default BulkActionsBar;
